import React, { useRef, useState } from 'react'
import { AUTH_BADGE_LABEL, AUTH_HEADING, BRAND_LOGO_ALT, brandLogo } from '../branding'
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { Link, useNavigate } from "react-router-dom";
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';

// Firebase
import { db } from '../firebase';
import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { collection, query, getDocs, where, doc, getDoc } from "firebase/firestore";
import { recordAdminLogin } from '../utils/adminPresence';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faEye, faEyeSlash, faLock } from '@fortawesome/free-solid-svg-icons'

function Login() {
    let navigate = useNavigate();
    const emailRef = useRef();
    const passwordRef = useRef();
    const [showProgress, setShowProgress] = useState(false);
    const [showSignInbtn, setShowSignInbtn] = useState(true);
    const [showPassword, setShowPassword] = useState(false);

    async function findAdminRecord(userID, email) {
        const directDoc = await getDoc(doc(db, "Admins", userID));
        if (directDoc.exists()) {
            return { id: directDoc.id, ...directDoc.data() };
        }

        const byUserId = await getDocs(query(collection(db, "Admins"), where("userID", "==", userID)));
        if (!byUserId.empty) {
            const foundDoc = byUserId.docs[0];
            return { id: foundDoc.id, ...foundDoc.data() };
        }

        if (email) {
            const byEmail = await getDocs(query(collection(db, "Admins"), where("userEmail", "==", email)));
            if (!byEmail.empty) {
                const foundDoc = byEmail.docs[0];
                return { id: foundDoc.id, ...foundDoc.data() };
            }
        }

        return null;
    }

    function getReadableAuthError(error) {
        if (!error) {
            return 'Something went wrong while signing you in.';
        }

        if (typeof error.message === 'string' && error.message.trim()) {
            return error.message.replace(/^Firebase:\s*/i, '').trim();
        }

        return 'Something went wrong while signing you in.';
    }

    async function SignInUser() {
        const email = emailRef.current.value;
        const password = passwordRef.current.value;
        const auth = getAuth();

        setShowProgress(true);
        setShowSignInbtn(false);

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const loggedInUser = userCredential.user;
            const adminRecord = await findAdminRecord(loggedInUser.uid, loggedInUser.email);

            if (!adminRecord) {
                await signOut(auth);
                setShowProgress(false);
                setShowSignInbtn(true);
                swal('Access denied', 'This account is not registered as an admin.', 'error');
                return;
            }

            if (String(adminRecord.accessStatus || 'active').toLowerCase() === 'disabled') {
                await signOut(auth);
                setShowProgress(false);
                setShowSignInbtn(true);
                swal('Access denied', 'This admin account has been disabled.', 'error');
                return;
            }

            try {
                await recordAdminLogin({
                    userId: loggedInUser.uid,
                    email: loggedInUser.email,
                    path: '/dashboard'
                });
            } catch (presenceError) {
                console.error('Failed to record admin login:', presenceError);
            }

            navigate("/dashboard");
        } catch (error) {
            setShowProgress(false);
            setShowSignInbtn(true);
            swal('Login failed', getReadableAuthError(error), 'error');
        }
    }

    return (
        <div className='d-flex loginContainer'>
            <div className='loginLeft'>
                <video src="https://res.cloudinary.com/ubunifu/video/upload/v1747845880/5683800-uhd_2160_3840_25fps_a0kaec.mp4" muted autoPlay loop></video>
                <div className="authMediaOverlay"></div>
                <div className="authMediaContent">
                    <span className="authBadge">{AUTH_BADGE_LABEL}</span>
                    <h2>{AUTH_HEADING}</h2>
                </div>
            </div>
            <div className='loginRight'>
                <div className="authPanel">
                    <Link to="/"><img src={brandLogo} alt={BRAND_LOGO_ALT} className='loginLogo' /></Link>
                    <h5>Welcome back!</h5>
                    <h6>Enter your staff email address and password to access your account.</h6>

                    <Form
                        onSubmit={(event) => {
                            event.preventDefault();
                            SignInUser();
                        }}
                    >
                        <Form.Label className="inputLabel">Email</Form.Label>
                        <InputGroup className="mb-3 authInputGroup">
                            <InputGroup.Text id="login-email-addon">
                                <FontAwesomeIcon icon={faEnvelope} className="theLoginIcon" />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Enter your email"
                                type="email"
                                className='loginForm'
                                ref={emailRef}
                                autoComplete="email"
                            />
                        </InputGroup>

                        <Form.Label className="inputLabel">Password</Form.Label>
                        <InputGroup className="mb-2 authInputGroup">
                            <InputGroup.Text id="login-password-addon">
                                <FontAwesomeIcon icon={faLock} className="theLoginIcon" />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Enter your Password"
                                type={showPassword ? "text" : "password"}
                                className='loginForm'
                                ref={passwordRef}
                                autoComplete="current-password"
                            />
                            <InputGroup.Text className="passwordToggleAddon">
                                <button
                                    type="button"
                                    className="passwordToggleBtn"
                                    onClick={() => setShowPassword((current) => !current)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="theLoginIcon" />
                                </button>
                            </InputGroup.Text>
                        </InputGroup>

                        <div className="loginActionRow">
                            <Link to="/reset-password" className='fgPassLink'>
                                Forgot password?
                            </Link>
                        </div>

                        {showSignInbtn &&
                            <button type="submit" className='signInbtnn'>
                                Sign In
                            </button>
                        }

                        {showProgress &&
                            <Button variant="primary" disabled className="signInbtProgress mt-3">
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                                <span> Logging in...</span>
                            </Button>
                        }
                    </Form>

                    <p className="authPanelNote">Protected with staff email and password access.</p>
                </div>
            </div>
        </div>
    )
}

export default Login;
